<div class="voucher-config-container">
  <div class="header">
    <div class="header-content">
      <div>
        <h1>Voucher Configuration</h1>
        <p>Manage and view all voucher configuration information</p>
      </div>
      <button class="create-btn" (click)="openCreateModal()">
        <i class="fas fa-plus"></i> Create Configuration
      </button>
    </div>
  </div>

  <!-- Filter Section -->
  <div class="filter-section">
    <div class="filter-header">
      <h3><i class="fas fa-filter"></i> Filter Configurations</h3>
      <button class="clear-filters-btn" (click)="clearFilter()" [disabled]="!filterPartnerId">
        <i class="fas fa-times"></i> Clear Filter
      </button>
    </div>
    
    <div class="filter-form">
      <div class="filter-row">
        <div class="filter-group">
          <label for="partnerFilter">Partner</label>
          <select 
            id="partnerFilter"
            [(ngModel)]="filterPartnerId" 
            class="filter-input">
            <option [value]="null">All Partners</option>
            <option *ngFor="let partner of partners" [value]="partner.id">
              {{ partner.name }}
            </option>
          </select>
        </div>
        
        <div class="filter-actions">
          <button class="apply-filters-btn" (click)="applyFilter()">
            <i class="fas fa-search"></i> Apply Filter
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage" class="error-message">
    <div class="error-content">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ errorMessage }}</span>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="loading" class="loading">
    <p>Loading voucher configurations...</p>
  </div>

  <!-- Table Container -->
  <div class="table-container" *ngIf="!loading && configurations.length > 0">
    <!-- Desktop Table View -->
    <div class="table-wrapper">
      <table class="config-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Partner</th>
            <th>Validity Period</th>
            <th>Bill Multiplier</th>
            <th>Default Multiplier</th>
            <th>Zones</th>
            <th>Voucher Amounts</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let config of configurations">
            <!-- Main row -->
            <tr class="config-row" [class.expanded]="isRowExpanded(config.id)">
              <td><strong>{{ config.name }}</strong></td>
              <td>{{ config.partnerName }}</td>
              <td>{{ config.validityPeriodInMonths }} months</td>
              <td>{{ config.billAmountMultiplier }}x</td>
              <td>{{ config.defaultMultiplier }}x</td>
              <td>{{ config.zones.length }} zone(s)</td>
              <td>{{ config.possibleVoucherAmounts.length }} amount(s)</td>
              <td>
                <div class="action-buttons">
                  <button 
                    class="expand-btn" 
                    (click)="toggleRow(config.id)"
                    [class.expanded]="isRowExpanded(config.id)" 
                    title="Expand to see details">
                    {{ isRowExpanded(config.id) ? '▼' : '▶' }}
                  </button>
                  <button 
                    class="edit-btn"
                    (click)="openEditModal(config); $event.stopPropagation()"
                    title="Edit Configuration">
                    <i class="fas fa-edit"></i>
                  </button>
                  <button 
                    class="delete-btn"
                    (click)="deleteConfiguration(config); $event.stopPropagation()"
                    title="Delete Configuration">
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
            
            <!-- Expanded row with full details -->
            <tr class="details-row" *ngIf="isRowExpanded(config.id)">
              <td colspan="8">
                <div class="config-details">
                  <div class="details-header">
                    <h4>Configuration Details</h4>
                  </div>
                  <div class="details-grid">
                    <div class="detail-item">
                      <label>ID:</label>
                      <span>{{ config.id }}</span>
                    </div>
                    <div class="detail-item">
                      <label>Name:</label>
                      <span>{{ config.name }}</span>
                    </div>
                    <div class="detail-item">
                      <label>Partner ID:</label>
                      <span>{{ config.partnerId }}</span>
                    </div>
                    <div class="detail-item">
                      <label>Partner Name:</label>
                      <span>{{ config.partnerName }}</span>
                    </div>
                    <div class="detail-item">
                      <label>Validity Period:</label>
                      <span>{{ config.validityPeriodInMonths }} months</span>
                    </div>
                    <div class="detail-item">
                      <label>Bill Amount Multiplier:</label>
                      <span>{{ config.billAmountMultiplier }}x</span>
                    </div>
                    <div class="detail-item">
                      <label>Default Multiplier:</label>
                      <span>{{ config.defaultMultiplier }}x</span>
                    </div>
                    <div class="detail-item full-width">
                      <label>Zones ({{ config.zones.length }}):</label>
                      <div class="list-items">
                        <span *ngFor="let zone of config.zones" class="list-item">
                          {{ zone.zoneName }} ({{ zone.zoneId }})
                        </span>
                        <span *ngIf="config.zones.length === 0" class="no-items">No zones configured</span>
                      </div>
                    </div>
                    <div class="detail-item full-width">
                      <label>Possible Voucher Amounts ({{ config.possibleVoucherAmounts.length }}):</label>
                      <div class="list-items">
                        <span *ngFor="let amount of config.possibleVoucherAmounts" class="list-item">
                          {{ formatCurrency(amount) }}
                        </span>
                        <span *ngIf="config.possibleVoucherAmounts.length === 0" class="no-items">No amounts configured</span>
                      </div>
                    </div>
                    <div class="detail-item full-width">
                      <label>Item Multipliers ({{ config.itemMultipliers.length }}):</label>
                      <div class="list-items">
                        <div *ngFor="let item of config.itemMultipliers" class="list-item">
                          <strong>{{ item.scrapItemName }}</strong> ({{ item.scrapItemId }}): {{ item.multiplier }}x
                        </div>
                        <span *ngIf="config.itemMultipliers.length === 0" class="no-items">No item multipliers configured</span>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </div>
  </div>

  <!-- No Data Message -->
  <div *ngIf="!loading && configurations.length === 0" class="no-data">
    <p>No voucher configurations found.</p>
  </div>
</div>

<!-- Create Modal -->
<div class="modal-overlay" *ngIf="showCreateModal">
  <div class="modal-content">
    <div class="modal-header">
      <h2>Create Voucher Configuration</h2>
      <button class="close-btn" (click)="closeCreateModal()">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div class="modal-body">
      <form (ngSubmit)="createConfiguration()">
        <div class="form-group">
          <label for="createName">Name *</label>
          <input 
            type="text" 
            id="createName"
            [(ngModel)]="configurationForm.name" 
            name="createName"
            required
            class="form-input">
        </div>

        <div class="form-group">
          <label for="createPartner">Partner *</label>
          <select 
            id="createPartner"
            [(ngModel)]="selectedPartnerId" 
            (change)="onPartnerSelect()"
            name="createPartner"
            required
            class="form-input">
            <option [value]="null">Select Partner</option>
            <option *ngFor="let partner of partners" [value]="partner.id">
              {{ partner.name }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="createValidity">Validity Period (Months) *</label>
          <input 
            type="number" 
            id="createValidity"
            [(ngModel)]="configurationForm.validityPeriodInMonths" 
            name="createValidity"
            min="1"
            required
            class="form-input">
        </div>

        <div class="form-group">
          <label for="createMultiplier">Bill Amount Multiplier *</label>
          <input 
            type="number" 
            id="createMultiplier"
            [(ngModel)]="configurationForm.billAmountMultiplier" 
            name="createMultiplier"
            min="0.01"
            step="0.01"
            required
            class="form-input">
        </div>

        <div class="form-group">
          <label for="createDefaultMultiplier">Default Multiplier *</label>
          <input 
            type="number" 
            id="createDefaultMultiplier"
            [(ngModel)]="configurationForm.defaultMultiplier" 
            name="createDefaultMultiplier"
            min="0"
            step="0.01"
            required
            class="form-input">
        </div>

        <div class="form-group">
          <label>Zones</label>
          <div class="array-input">
            <div *ngFor="let zone of configurationForm.zones; let i = index" class="array-item">
              <select 
                [(ngModel)]="zone.zoneId" 
                [name]="'zoneId' + i"
                (change)="onZoneSelect(i)"
                class="form-input">
                <option [value]="''">Select Zone</option>
                <option *ngFor="let availableZone of zones" [value]="availableZone.id">
                  {{ availableZone.name }}
                </option>
              </select>
              <button type="button" class="remove-btn" (click)="removeZone(i)">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <button type="button" class="add-btn" (click)="addZone()">
              <i class="fas fa-plus"></i> Add Zone
            </button>
          </div>
        </div>

        <div class="form-group">
          <label>Possible Voucher Amounts</label>
          <div class="array-input">
            <div *ngFor="let amount of configurationForm.possibleVoucherAmounts; let i = index" class="array-item">
              <input 
                type="number" 
                [(ngModel)]="configurationForm.possibleVoucherAmounts[i]" 
                [name]="'amount' + i"
                placeholder="Amount (e.g., 500, 1000)"
                min="0"
                step="any"
                class="form-input">
              <button type="button" class="remove-btn" (click)="removeVoucherAmount(i)">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <button type="button" class="add-btn" (click)="addVoucherAmount()">
              <i class="fas fa-plus"></i> Add Amount
            </button>
          </div>
        </div>

        <div class="form-group">
          <label>Item Multipliers</label>
          <div class="array-input">
            <div *ngFor="let item of configurationForm.itemMultipliers; let i = index" class="array-item">
              <select 
                [(ngModel)]="item.scrapItemId" 
                [name]="'scrapItemId' + i"
                (change)="onScrapItemSelect(i)"
                class="form-input">
                <option [value]="''">Select Scrap Item</option>
                <option *ngFor="let scrapItem of scrapItems" [value]="scrapItem.scrapItemId">
                  {{ scrapItem.scrapName }}
                </option>
              </select>
              <input 
                type="number" 
                [(ngModel)]="item.multiplier" 
                [name]="'multiplier' + i"
                placeholder="Multiplier"
                min="0"
                step="0.01"
                class="form-input">
              <button type="button" class="remove-btn" (click)="removeItemMultiplier(i)">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <button type="button" class="add-btn" (click)="addItemMultiplier()">
              <i class="fas fa-plus"></i> Add Item Multiplier
            </button>
          </div>
        </div>

        <div class="modal-actions">
          <button type="button" class="cancel-btn" (click)="closeCreateModal()">Cancel</button>
          <button type="submit" class="submit-btn" [disabled]="loading">Create</button>
        </div>
      </form>
    </div>
  </div>
</div>

<!-- Edit Modal -->
<div class="modal-overlay" *ngIf="showEditModal">
  <div class="modal-content">
    <div class="modal-header">
      <h2>Edit Voucher Configuration</h2>
      <button class="close-btn" (click)="closeEditModal()">
        <i class="fas fa-times"></i>
      </button>
    </div>
    <div class="modal-body">
      <form (ngSubmit)="updateConfiguration()">
        <div class="form-group">
          <label for="editName">Name *</label>
          <input 
            type="text" 
            id="editName"
            [(ngModel)]="configurationForm.name" 
            name="editName"
            required
            class="form-input">
        </div>

        <div class="form-group">
          <label for="editPartner">Partner *</label>
          <select 
            id="editPartner"
            [(ngModel)]="selectedPartnerId" 
            (change)="onPartnerSelect()"
            name="editPartner"
            required
            class="form-input">
            <option [value]="null">Select Partner</option>
            <option *ngFor="let partner of partners" [value]="partner.id">
              {{ partner.name }}
            </option>
          </select>
        </div>

        <div class="form-group">
          <label for="editValidity">Validity Period (Months) *</label>
          <input 
            type="number" 
            id="editValidity"
            [(ngModel)]="configurationForm.validityPeriodInMonths" 
            name="editValidity"
            min="1"
            required
            class="form-input">
        </div>

        <div class="form-group">
          <label for="editMultiplier">Bill Amount Multiplier *</label>
          <input 
            type="number" 
            id="editMultiplier"
            [(ngModel)]="configurationForm.billAmountMultiplier" 
            name="editMultiplier"
            min="0.01"
            step="0.01"
            required
            class="form-input">
        </div>

        <div class="form-group">
          <label for="editDefaultMultiplier">Default Multiplier *</label>
          <input 
            type="number" 
            id="editDefaultMultiplier"
            [(ngModel)]="configurationForm.defaultMultiplier" 
            name="editDefaultMultiplier"
            min="0"
            step="0.01"
            required
            class="form-input">
        </div>

        <div class="form-group">
          <label>Zones</label>
          <div class="array-input">
            <div *ngFor="let zone of configurationForm.zones; let i = index" class="array-item">
              <select 
                [(ngModel)]="zone.zoneId" 
                [name]="'editZoneId' + i"
                (change)="onZoneSelect(i)"
                class="form-input">
                <option [value]="''">Select Zone</option>
                <option *ngFor="let availableZone of zones" [value]="availableZone.id">
                  {{ availableZone.name }}
                </option>
              </select>
              <button type="button" class="remove-btn" (click)="removeZone(i)">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <button type="button" class="add-btn" (click)="addZone()">
              <i class="fas fa-plus"></i> Add Zone
            </button>
          </div>
        </div>

        <div class="form-group">
          <label>Possible Voucher Amounts</label>
          <div class="array-input">
            <div *ngFor="let amount of configurationForm.possibleVoucherAmounts; let i = index" class="array-item">
              <input 
                type="number" 
                [(ngModel)]="configurationForm.possibleVoucherAmounts[i]" 
                [name]="'editAmount' + i"
                placeholder="Amount (e.g., 500, 1000)"
                min="0"
                step="any"
                class="form-input">
              <button type="button" class="remove-btn" (click)="removeVoucherAmount(i)">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <button type="button" class="add-btn" (click)="addVoucherAmount()">
              <i class="fas fa-plus"></i> Add Amount
            </button>
          </div>
        </div>

        <div class="form-group">
          <label>Item Multipliers</label>
          <div class="array-input">
            <div *ngFor="let item of configurationForm.itemMultipliers; let i = index" class="array-item">
              <select 
                [(ngModel)]="item.scrapItemId" 
                [name]="'editScrapItemId' + i"
                (change)="onScrapItemSelect(i)"
                class="form-input">
                <option [value]="''">Select Scrap Item</option>
                <option *ngFor="let scrapItem of scrapItems" [value]="scrapItem.scrapItemId">
                  {{ scrapItem.scrapName }}
                </option>
              </select>
              <input 
                type="number" 
                [(ngModel)]="item.multiplier" 
                [name]="'editMultiplier' + i"
                placeholder="Multiplier"
                min="0"
                step="0.01"
                class="form-input">
              <button type="button" class="remove-btn" (click)="removeItemMultiplier(i)">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <button type="button" class="add-btn" (click)="addItemMultiplier()">
              <i class="fas fa-plus"></i> Add Item Multiplier
            </button>
          </div>
        </div>

        <div class="modal-actions">
          <button type="button" class="cancel-btn" (click)="closeEditModal()">Cancel</button>
          <button type="submit" class="submit-btn" [disabled]="loading">Update</button>
        </div>
      </form>
    </div>
  </div>
</div>

